'use client';

import React from 'react';

interface CaseStudyCardProps {
  type: string;
  platform: string;
  metric: string;
  metricLabel: string;
  beforeItems: string[];
  afterItems: string[];
  description: string;
  timeframe: string;
  gradientFrom: string;
  gradientTo: string;
  icon: React.ReactNode;
  delay: string;
}

function CaseStudyCard({ type, platform, metric, metricLabel, beforeItems, afterItems, description, timeframe, gradientFrom, gradientTo, icon, delay }: CaseStudyCardProps) {
  return (
    <div className={`reveal ${delay} glass-card rounded-3xl overflow-hidden`}>
      {/* Top */}
      <div className={`bg-gradient-to-br ${gradientFrom} ${gradientTo} px-7 pt-7 pb-5`}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-sm font-semibold text-white">{type}</div>
            <div className="text-xs text-neutral-400">{platform}</div>
          </div>
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center text-blue-400"
            style={{ background: 'rgba(59, 130, 246, 0.1)', border: '1px solid rgba(59, 130, 246, 0.2)' }}
          >
            {icon}
          </div>
        </div>
        <div className="text-5xl font-black gradient-text leading-none mb-1">{metric}</div>
        <div className="text-xs text-neutral-400 font-medium">{metricLabel}</div>
      </div>

      {/* Bottom */}
      <div className="px-7 py-5">
        <div className="grid grid-cols-2 gap-3 mb-5">
          <div className="rounded-xl p-3.5" style={{ background: 'rgba(239, 68, 68, 0.05)', border: '1px solid rgba(239, 68, 68, 0.1)' }}>
            <div className="text-xs text-neutral-500 uppercase tracking-wide mb-1 font-medium">Before</div>
            {beforeItems.map((item, i) => (
              <div key={i} className={i === 0 ? 'text-sm font-bold text-white' : 'text-xs text-neutral-500'}>{item}</div>
            ))}
          </div>
          <div className="rounded-xl p-3.5" style={{ background: 'rgba(59, 130, 246, 0.05)', border: '1px solid rgba(59, 130, 246, 0.15)' }}>
            <div className="text-xs text-neutral-500 uppercase tracking-wide mb-1 font-medium">After</div>
            {afterItems.map((item, i) => (
              <div key={i} className={i === 0 ? 'text-sm font-bold text-blue-300' : 'text-xs text-neutral-500'}>{item}</div>
            ))}
          </div>
        </div>
        <p className="text-sm text-neutral-400 leading-relaxed mb-4">{description}</p>
        <div className="flex items-center gap-2">
          <div className="w-1.5 h-1.5 rounded-full bg-green-500" style={{ boxShadow: 'rgba(34, 197, 94, 0.6) 0px 0px 6px' }} />
          <span className="text-xs text-neutral-500">{timeframe}</span>
        </div>
      </div>
    </div>
  );
}

export default function CaseStudiesSection() {
  const cases = [
    {
      type: 'Creator Brand',
      platform: 'TikTok & Instagram',
      metric: '+42,500%',
      metricLabel: 'Follower Growth',
      beforeItems: ['2K followers', '$0 monthly revenue'],
      afterItems: ['850K+ followers', '$45K/month revenue'],
      description: 'A lifestyle creator with zero monetization transformed into a full-time income machine using our AI content system.',
      timeframe: 'Achieved in 5 months',
      gradientFrom: 'from-blue-600/20',
      gradientTo: 'to-blue-900/10',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <polyline points="22 7 13.5 15.5 8.5 10.5 2 17" />
          <polyline points="16 7 22 7 22 13" />
        </svg>
      ),
      delay: 'reveal-delay-1',
    },
    {
      type: 'SaaS Startup',
      platform: 'LinkedIn & X',
      metric: '-89%',
      metricLabel: 'Time Saved Weekly',
      beforeItems: ['3 hrs per post', 'Fully manual workflow'],
      afterItems: ['20 min per post', 'Fully automated'],
      description: 'A bootstrapped SaaS went from drowning in content tasks to publishing 3x more with a fraction of the effort.',
      timeframe: 'Achieved in 3 weeks',
      gradientFrom: 'from-purple-600/20',
      gradientTo: 'to-purple-900/10',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="12" cy="12" r="10" />
          <polyline points="12 6 12 12 16 14" />
        </svg>
      ),
      delay: 'reveal-delay-2',
    },
    {
      type: 'E-Commerce Brand',
      platform: 'Multi-platform',
      metric: '+962%',
      metricLabel: 'Revenue in 90 Days',
      beforeItems: ['$8K/month', 'Inconsistent sales'],
      afterItems: ['$85K/month', 'Predictable revenue'],
      description: 'An e-commerce brand scaled from struggling to thriving with AI-powered content funnels and automated outreach.',
      timeframe: 'Achieved in 90 days',
      gradientFrom: 'from-blue-600/15',
      gradientTo: 'to-purple-600/15',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <line x1="12" x2="12" y1="2" y2="22" />
          <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
        </svg>
      ),
      delay: 'reveal-delay-3',
    },
  ];

  return (
    <section
      id="results"
      className="relative py-24 sm:py-32 overflow-hidden"
      style={{ background: 'linear-gradient(rgb(5, 5, 5), rgb(6, 8, 16), rgb(5, 5, 5))' }}
    >
      {/* Background orb */}
      <div
        className="absolute pointer-events-none"
        style={{ top: '30%', right: '-5%', width: 500, height: 500, borderRadius: '50%', background: 'radial-gradient(circle, rgba(157, 76, 221, 0.06) 0%, transparent 70%)', filter: 'blur(40px)' }}
      />

      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        {/* Header */}
        <div className="reveal text-center mb-16">
          <span className="text-xs font-semibold tracking-[0.2em] uppercase text-purple-400 mb-4 block">Case Studies</span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight mb-4" style={{ letterSpacing: '-0.02em' }}>Real Brands. Real Results.</h2>
          <p className="text-base text-neutral-500 max-w-xl mx-auto">Transformations we&apos;ve engineered for clients using AI-powered growth systems.</p>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {cases.map((c) => (
            <CaseStudyCard key={c.type} {...c} />
          ))}
        </div>
      </div>
    </section>
  );
}
